import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "config/firebase";
import { Skeleton } from "@mui/material";
import type { BreadcrumbComponentType } from "use-react-router-breadcrumbs";
import { useNonAdminClients } from "@/hooks/clients/useNonAdminClients";


export const ProjectBreadcrumb: BreadcrumbComponentType<"projectId"> = ({
  match,
}) => {
  const [projectName, setProjectName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const { clients } = useNonAdminClients();

  const flatProjects = clients?.flatMap((client) => client.projects);

  useEffect(() => {
    const fetchProjectName = async () => {
      if (!match.params.projectId) return;

      try {
        setIsLoading(true);
        const project = flatProjects?.find(
          (project) => project?.id === match.params.projectId
        );
        if (project) {
          setProjectName(project.name || "Project Details");
        } else {
          setProjectName("Project Details");
        }
      } catch (error) {
        console.error("Error resolving project name:", error);
        setProjectName("Project Details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectName();
  }, [match.params.projectId]);

  if (isLoading) {
    return (
      <Skeleton width={100} height={24} sx={{ display: "inline-block" }} />
    );
  }

  return <span>{projectName}</span>;
};


export const BugReportBreadcrumb: BreadcrumbComponentType<"bugReportId"> = ({
  match,
}) => {
  const [bugReportTitle, setBugReportTitle] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBugReport = async () => {
      if (!match.params.bugReportId) return;

      try {
        setIsLoading(true);
        const bugReportDoc = await getDoc(
          doc(db, "bugReports", match.params.bugReportId)
        );
        if (bugReportDoc.exists()) {
          setBugReportTitle(
            `Bug Report #${match.params.bugReportId.slice(0, 8)}`
          );
        } else {
          setBugReportTitle("Bug Report");
        }
      } catch (error) {
        console.error("Error resolving bug report:", error);
        setBugReportTitle("Bug Report");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBugReport();
  }, [match.params.bugReportId]);

  if (isLoading) {
    return (
      <Skeleton width={100} height={24} sx={{ display: "inline-block" }} />
    );
  }

  return <span>{bugReportTitle}</span>;
};


export const FeatureRequestBreadcrumb: BreadcrumbComponentType<
  "featureRequestId"
> = ({ match }) => {
  const [featureRequestTitle, setFeatureRequestTitle] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFeatureRequest = async () => {
      if (!match.params.featureRequestId) return;

      try {
        setIsLoading(true);
        const projectId = match.pathname.split("/")[3];
        const featureRequestDoc = await getDoc(
          doc(
            db,
            "clients",
            "projects",
            projectId,
            "featureRequests",
            match.params.featureRequestId
          )
        );
        if (featureRequestDoc.exists()) {
          setFeatureRequestTitle(
            `Feature Request #${match.params.featureRequestId.slice(0, 8)}`
          );
        } else {
          setFeatureRequestTitle("Feature Request");
        }
      } catch (error) {
        console.error("Error resolving feature request:", error);
        setFeatureRequestTitle("Feature Request");
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeatureRequest();
  }, [match.params.featureRequestId, match.pathname]);

  if (isLoading) {
    return (
      <Skeleton width={100} height={24} sx={{ display: "inline-block" }} />
    );
  }

  return <span>{featureRequestTitle}</span>;
};


export const ClientBreadcrumb: BreadcrumbComponentType<"clientId"> = ({
  match,
}) => {
  const [clientTitle, setClientTitle] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchClient = async () => {
      if (!match.params.clientId) return;

      try {
        setIsLoading(true);
        const clientDoc = await getDoc(
          doc(db, "clients", match.params.clientId)
        );
        if (clientDoc.exists()) {
          setClientTitle(
            `Client Management #${match.params.clientId.slice(0, 8)}`
          );
        } else {
          setClientTitle("Client Management");
        }
      } catch (error) {
        console.error("Error resolving client:", error);
        setClientTitle("Client Management");
      } finally {
        setIsLoading(false);
      }
    };

    fetchClient();
  }, [match.params.clientId]);

  if (isLoading) {
    return (
      <Skeleton width={100} height={24} sx={{ display: "inline-block" }} />
    );
  }

  return <span>{clientTitle}</span>;
};
